<template>
    <div>
        <h1 class="display-heading">{{pageTitle}}</h1>

        <!-- #region Create -->
        <div class="bs-stepper" v-if="isAdd">
            <form data-vv-scope="submit-manual-reimbursement" @submit.prevent="validateRequestForm">
                <div class="form-group row">
                    <label class="col-sm-3 col-xl-2 col-form-label">Patient</label>
                    <div class="col-sm-9 col-xl-10">
                        <select placeholder="Patient" name="patient" v-model="patientUserId"
                            data-vv-as="patient" data-vv-validate-on="blur"
                            v-validate="'required'" :class="{
                            'form-control': true,
                            'is-invalid':  validationErrors.has('submit-manual-reimbursement.patient')
                        }">
                            <option :value="null" disabled hidden>Please Select...</option>
                            <option value="-">Self</option>
                            <option v-for="(dependent, index) in dependentList" :key="index"
                                :value="dependent.userId">
                                {{dependent.fullName}}
                            </option>
                        </select>
                        <div v-show="validationErrors.has('submit-manual-reimbursement.patient')" class="invalid-feedback">
                            {{ validationErrors.first('submit-manual-reimbursement.patient') }}
                        </div>
                    </div>
                </div>
                <div class="form-group row">
                    <label class="col-sm-3 col-xl-2 col-form-label">Uploaded Documents</label>
                    <div class="col-sm-9 col-xl-10">
                        <div v-if="files.length <= 0">No files uploaded.</div>
                        <div class="d-flex justify-content-between" v-for="(file, index) in files" :key="'file' + index">
                            <span>{{ file.name }}</span>
                            <a
                                href="javascript: void(0);"
                                class="delete-button"
                                @click="deleteFile(index, index)"
                            >
                                <i style="color:red;" class="fas fa-trash-alt"></i>
                            </a>
                        </div>
                    </div>
                </div>
                <div class="form-group row" v-if="stopUploading === false">
                    <label class="col-sm-3 col-xl-2 col-form-label">Document File</label>
                    <div class="col-sm-9 col-xl-10" v-if="addedFile === false">
                        <div>
                            <input id="uploadFileInput" type="file" name="reportFile">
                            <button type="button" @click="addFile" class="btn btn-primary btn-primary-insv ml-2">Add</button>
                        </div>
                    </div>
                    <div class="col-sm-9 col-xl-10" v-else>
                        <span>Do you have more files to add?</span>
                        <button type="button" @click="resetFile" class="btn btn-primary btn-primary-insv mx-2">Yes</button>
                        <button type="button" @click="focusNext" class="btn btn-secondary">No</button>
                    </div>
                </div>
                <div class="form-group row">
                    <label class="col-sm-3 col-xl-2 col-form-label">Consultation / Transaction Date</label>
                    <div class="col-sm-9 col-xl-10">
                        <input type="text" placeholder="DD/MM/YYYY" id="reimbursementDate" name="reimbursementDate" v-model="reimbursementDate"
                            data-vv-as="reimbursement date" data-vv-validate-on="blur" v-mask="'##/##/####'"
                            v-validate="'required|date_format:DD/MM/YYYY'" :class="{
                                'form-control': true,
                                'is-invalid':  validationErrors.has('submit-manual-reimbursement.reimbursementDate')
                            }">
                        <div v-show="validationErrors.has('submit-manual-reimbursement.reimbursementDate')" class="invalid-feedback">
                            {{ validationErrors.first('submit-manual-reimbursement.reimbursementDate') }}
                        </div>
                    </div>
                </div>
                <div class="form-group row">
                    <label class="col-sm-3 col-xl-2 col-form-label">Reimbursement Amount</label>
                    <div class="col-sm-9 col-xl-10">
                        <input type="number" step=".01" placeholder="0.00" id="reimbursementAmount" name="reimbursementAmount" v-model="requestAmount"
                            data-vv-as="reimbursement amount" data-vv-validate-on="blur"
                            v-validate="'required|decimal:2|min_value:1'"
                            :class="{
                                'form-control': true,
                                'is-invalid':  validationErrors.has('submit-manual-reimbursement.reimbursementAmount')
                            }">
                        <div v-show="validationErrors.has('submit-manual-reimbursement.reimbursementAmount')" class="invalid-feedback">
                            {{ validationErrors.first('submit-manual-reimbursement.reimbursementAmount') }}
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col-sm-9 offset-sm-3 col-xl-10 offset-xl-2">
                        <p v-show="shouldShowSubmitMessage" :class="{
                            'text-danger': submitHasError,
                            'mb-2': true
                        }">
                            {{submitMessage}}
                        </p>
                    </div>
                </div>

                <div class="row">
                    <a href="#/user/manual-reimbursement" class="btn btn-secondary mr-2 mb-2 router-link-active">
                        Back to List
                    </a>
                    <button type="button" class="btn btn-primary btn-primary-insv mr-2 mb-2" @click="submitManualReimbursement">Save
                        and Upload Report
                    </button>
                </div>
            </form>
        </div>
        <!-- #endregion -->

        <!-- #region See Deatails -->
        <div v-else>
            <div class="form-group row">
                <label class="col-sm-3 col-xl-2 col-form-label">Patient</label>
                <div class="col-sm-9 col-xl-10">
                    <input type="text" class="form-control" v-model="patientName" disabled="disabled">
                </div>
            </div>
            <div class="form-group row">
                <label class="col-sm-3 col-xl-2 col-form-label">Company</label>
                <div class="col-sm-9 col-xl-10">
                    <input type="text" class="form-control" v-model="companyName" disabled="disabled">
                </div>
            </div>
            <div class="form-group row">
                <label class="col-sm-3 col-xl-2 col-form-label">Uploaded Documents</label>
                <div class="col-sm-9 col-xl-10">
                    <div v-if="files.length <= 0">No files uploaded.</div>
                    <template v-else>
                        <a v-for="(fileId, index) in files" class="btn btn-primary btn-primary-insv mr-2" :key="index" :href="`${apiPath}ManualReimbursement/DownloadFileById?fileId=${fileId}&token=${token}`" download>Download File {{ index + 1 }}</a>
                    </template>
                </div>
            </div>
            <div class="form-group row">
                <label class="col-sm-3 col-xl-2 col-form-label">Consultation / Transaction Date</label>
                <div class="col-sm-9 col-xl-10">
                    <input type="text" class="form-control" v-model="reimbursementDate" disabled="disabled">
                </div>
            </div>
            <div class="form-group row">
                <label class="col-sm-3 col-xl-2 col-form-label">Request Amount</label>
                <div class="col-sm-9 col-xl-10">
                    <input type="text" class="form-control" v-model="requestAmount" disabled="disabled">
                </div>
            </div>
            <div class="form-group row">
                <label class="col-sm-3 col-xl-2 col-form-label">Approved Amount</label>
                <div class="col-sm-9 col-xl-10">
                    <input type="text" class="form-control" v-model="approvedAmount" disabled="disabled">
                </div>
            </div>
            <div class="form-group row">
                <label class="col-sm-3 col-xl-2 col-form-label">Status</label>
                <div class="col-sm-9 col-xl-10">
                    <span class="badge text-nowrap" :class="statusBadgeClass">
                        {{ statusDisplay }}
                    </span>
                </div>
            </div>
            <div class="form-group row">
                <label class="col-sm-3 col-xl-2 col-form-label">Remarks</label>
                <div class="col-sm-9 col-xl-10">
                    <input type="text" class="form-control" v-model="remark" disabled="disabled">
                </div>
            </div>
            <div>
                <a href="#/user/manual-reimbursement" class="btn btn-secondary mb-2 router-link-active">
                    Back to List
                </a>
            </div>
        </div>
        <!-- #endregion -->
    </div>
</template>

<script>
import {
    convertDateToDDMMYYYY,
    convertDateToIso8601
} from '@/utilities';
import { apiPath } from '@/config';
import Cookies from 'js-cookie';
import $ from 'jquery';
import {
    mask
} from 'vue-the-mask';
import popupModal from '@/components/popup-modal';
import permissible from '@/components/permissible';
import {
    genericStatus,
    getEnumDisplay,
    getEnumBadgeClass
} from '@/config/enums';
// import _ from 'lodash';
export default {
    'name': '',
    'directives': {
        mask
    },
    'components': {
        popupModal,
        permissible
    },
    'computed': {
        manualReimbursementId () {
            return this.$route.name === 'user-manual-reimbursement-detail' ? this.$route.params.id : null;
        },
        isAdd () {
            return this.$route.name === 'user-manual-reimbursement-add';
        },
        pageTitle () {
            return this.isAdd ? 'New Reimbursement Request' : 'View Reimbursement Request';
        },
        shouldShowSubmitMessage () {
            return this.submitMessage ? this.submitMessage.length > 0 : false;
        },
        token () {
            return Cookies.get('t');
        }
    },
    mounted () {
        $('[data-toggle="tooltip"]').tooltip();
        if (!this.isAdd && this.manualReimbursementId) {
            this.axios.get('ManualReimbursement/GetManualReimbursementDetail', {
                'params': {
                    'manualReimbursementId': this.manualReimbursementId
                }
            }).then(({
                data
            }) => {
                let reimbursementData = data.data;
                this.userName = reimbursementData.un;
                this.patientName = reimbursementData.pn;
                this.companyName = reimbursementData.cn;
                this.reimbursementDate = convertDateToDDMMYYYY(reimbursementData.rd);
                this.requestAmount = reimbursementData.ra.toFixed(2);
                this.approvedAmount = reimbursementData.aa.toFixed(2);
                this.status = reimbursementData.s;
                this.remark = reimbursementData.r ? reimbursementData.r : '-';
                this.files = reimbursementData.f;
                this.statusDisplay = getEnumDisplay(genericStatus, reimbursementData.s);
                this.statusBadgeClass = getEnumBadgeClass(genericStatus, reimbursementData.s);
            }).catch(() => {});
        } else {
            this.axios.get('EmployeeTier/GetDependentList')
                .then(({
                    data
                }) => {
                    if (data.data) {
                        this.dependentList = data.data.map((dependent) => ({
                            'userId': dependent.ui,
                            'fullName': dependent.fn
                        }));
                    }
                }).catch(() => {});
        }
    },
    data () {
        return {
            'apiPath': apiPath,
            'patientUserId': null,
            'userName': null,
            'patientName': null,
            'companyName': null,
            'reimbursementDate': null,
            'requestAmount': null,
            'approvedAmount': null,
            'remark': null,
            'status': null,
            'files': [],
            'statusDisplay': '',
            'statusBadgeClass': '',
            'dependentList': [],
            'addedFile': false,
            'stopUploading': false,
            'isSaving': false,
            'submitMessage': '',
            'submitHasError': false
        };
    },
    'methods': {
        validateRequestForm () {
            return this.$validator.validateAll('tier');
        },
        submitManualReimbursement () {
            this.validateRequestForm().then((isValidate) => {
                if (isValidate) {
                    if (this.files.length <= 0) {
                        this.submitMessage = 'No files uploaded!';
                        return;
                    }

                    let formData = new FormData();
                    if (this.patientUserId !== '-') {
                        formData.append('PatientUserId', this.patientUserId);
                    }
                    formData.append('ReimbursementDate', convertDateToIso8601(this.reimbursementDate));
                    formData.append('Amount', this.requestAmount);
                    for (const file of this.files) {
                        formData.append('Documents', file);
                    }
                    this.submitMessage = 'Sending reimbursement request...';
                    this.axios.post('ManualReimbursement/CreateUserManualReimbursement', formData, {
                        'headers': {
                            'Content-Type': 'multipart/form-data'
                        } }).then(() => {
                        this.submitMessage = 'Successfully sent reimbursement request.';
                        this.$router.push(
                            {
                                'name': 'user-manual-reimbursement'
                            });
                    }).catch((error) => {
                        if (error.response.data) {
                            this.submitMessage = error.response.data.message;
                        } else {
                            this.submitMessage = 'Error sending reimbursement request. Please try again.';
                        }
                    });
                } else {
                    this.submitMessage = 'You have error in the form. Please check your input(s) before submitting the form.';
                }
            });
        },
        addFile () {
            let file = document.querySelector('#uploadFileInput');
            if (file.files[0]) {
                this.files.push(file.files[0]);
                this.addedFile = true;
            }
        },
        resetFile () {
            this.addedFile = false;
        },
        focusNext () {
            document.getElementById('reimbursementDate').focus();
            this.stopUploading = true;
        },
        deleteFile (index) {
            this.files.splice(index, 1);
        }
    },
    'watch': {
        'form.hfl': function (value) {
            this.form.sb = value;
            this.form.db = value;
        }
    }
};

</script>

<style scoped>
.active .bs-stepper-circle {
    background-color: #009344;
}

.bs-stepper .step-trigger:focus {
  color: #009344;
  outline: none;
}
</style>
